import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { amountToMinor, minorToAmount, type ApDebitNoteDto, type ApOpenInvoiceDto, vnDate } from "@hisense/shared";
import { useFinanceAccess } from "@/components/finance/FinanceRules";
import { debitNoteOpenInvoices, useDebitNotes } from "@/api/gl";
import { apiFetch, getLocalizedError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { formatMoney } from "@/domain/money";

/**
 * Phiếu ghi nợ nhà cung cấp trên trang Tài chính (17/09/2026).
 *
 * Phiếu KHÔNG lập tay ở đây: nó sinh khi khiếu nại nhà cung cấp được duyệt có tiền. Kế toán làm ba việc,
 * đúng cách SAP / Business Central xử lý vendor debit memo: ghi sổ (chọn ngày → kỳ), trừ vào hoá đơn nhà
 * cung cấp đã ghi sổ, và khi sai thì bỏ trừ / đảo (đảo sinh phiếu nháp thay thế). Mọi con số — phần đã
 * trừ, phần còn phải trả của hoá đơn — do máy chủ tính; ở đây chỉ nhận và hiển thị.
 */

type Dialog =
  | { kind: "post"; note: ApDebitNoteDto }
  | { kind: "reverse"; note: ApDebitNoteDto }
  | { kind: "apply"; note: ApDebitNoteDto }
  | { kind: "unapply"; note: ApDebitNoteDto; applicationId: string; label: string };

const MONEY = /^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/;
// Ngày chứng từ mặc định: hôm nay theo giờ Việt Nam (20/09/2026), không theo đồng hồ máy người dùng.
const localToday = () => vnDate();
const minor = (amount: string) => amountToMinor(amount, 2);
/** Số gợi ý trong ô nhập: luôn hai số lẻ ("45.50", không "45.5"). */
const fixed2 = (value: number) => {
  const [whole, decimals = ""] = minorToAmount(value, 2).split(".");
  return `${whole}.${decimals.padEnd(2, "0")}`;
};
const money = (amount: string, currency: string) => formatMoney(minor(amount), currency);

export function DebitNotesPanel({ q, page, onPage }: { q: string; page: number; onPage: (page: number) => void }) {
  const { t } = useTranslation();
  const query = useDebitNotes(q, page);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  // MỘT bộ giải quyền: admin luôn qua `canAccess`, tài khoản theo vai trò dùng bộ mẫu, tài khoản tuỳ chỉnh dùng ô tick.
  const canDecide = useFinanceAccess()("approve");
  const rows = query.data?.data ?? [];

  if (query.isLoading) return <LoadingState />;
  if (query.error) return <ErrorState message={t("gl.error")} onRetry={() => void query.refetch()} />;
  return (
    <section className="space-y-3">
      <p className="text-sm text-ink-soft">{t("gl.dnIntro")}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-soft">{t("gl.dnEmpty")}</p>
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-sunken">
              <tr>
                <th className="p-3 text-left">{t("gl.dnNo")}</th>
                <th className="p-3 text-left">{t("gl.dnClaim")} · {t("gl.dnSupplier")}</th>
                <th className="whitespace-nowrap p-3 text-right">{t("gl.dnAmount")}</th>
                <th className="whitespace-nowrap p-3 text-right">{t("gl.dnApplied")}</th>
                <th className="whitespace-nowrap p-3 text-right">{t("gl.dnUnapplied")}</th>
                <th className="whitespace-nowrap p-3 text-left">{t("gl.dnDate")}</th>
                <th className="p-3 text-right">
                  <span className="sr-only">{t("gl.post")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => {
                const active = n.applications.filter((a) => a.removedAt == null);
                return (
                  <tr key={n.id} className="border-t border-line align-top">
                    <td className="p-3">
                      <span className="font-mono">{n.documentNo}</span>
                      <span className="block text-xs text-ink-soft">{t(`gl.${n.status}`)}</span>
                      {n.replacesNo && <span className="block text-xs text-ink-soft">{t("gl.dnReplaces", { no: n.replacesNo })}</span>}
                    </td>
                    <td className="p-3">
                      <Link className="font-mono text-brand underline" to={`/supplier-claims/${n.claimId}`}>
                        {n.claimNo}
                      </Link>
                      <span className="block">{n.supplierName}</span>
                      {n.applications.length > 0 && (
                        <ul className="mt-2 space-y-1 text-xs">
                          {n.applications.map((a) => (
                            <li key={a.id}>
                              {/* Chỉ gạch phần hoá đơn và số tiền đã bỏ; lý do bỏ vẫn đọc được bình thường. */}
                              <span className={a.removedAt != null ? "text-ink-faint line-through" : ""}>
                                <span className="font-mono">{a.invoiceNo}</span> · <span className="font-mono tabular">{money(a.amount, n.currency)}</span>
                              </span>
                              {a.removedAt != null ? (
                                <span className="text-ink-soft"> · {t("gl.dnRemoved")}: {a.removedReason}</span>
                              ) : (
                                canDecide && (
                                  <button type="button" className="ml-2 text-brand underline" onClick={() => setDialog({ kind: "unapply", note: n, applicationId: a.id, label: `${a.invoiceNo} · ${money(a.amount, n.currency)}` })}>
                                    {t("gl.dnUnapply")}
                                  </button>
                                )
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="p-3 text-right font-mono tabular">{money(n.amount, n.currency)}</td>
                    <td className="p-3 text-right font-mono tabular">{money(n.appliedAmount, n.currency)}</td>
                    <td className="p-3 text-right font-mono tabular">{money(n.unappliedAmount, n.currency)}</td>
                    <td className="p-3 font-mono tabular">{n.noteDate ?? "—"}</td>
                    <td className="p-3">
                      {canDecide && (
                        <div className="flex flex-col items-end gap-1">
                          {n.status === "Draft" && (
                            <Button size="sm" onClick={() => setDialog({ kind: "post", note: n })}>
                              {t("gl.post")}
                            </Button>
                          )}
                          {n.status === "Posted" && minor(n.unappliedAmount) > 0 && (
                            <Button size="sm" variant="secondary" onClick={() => setDialog({ kind: "apply", note: n })}>
                              {t("gl.dnApply")}
                            </Button>
                          )}
                          {n.status === "Posted" && (
                            <Button size="sm" variant="secondary" disabled={active.length > 0} title={active.length > 0 ? t("gl.appliedError") : undefined} onClick={() => setDialog({ kind: "reverse", note: n })}>
                              {t("gl.dnReverse")}
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {(page > 1 || query.data?.hasMore) && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
            ‹
          </Button>
          <Button size="sm" variant="secondary" disabled={!query.data?.hasMore} onClick={() => onPage(page + 1)}>
            ›
          </Button>
        </div>
      )}
      {dialog?.kind === "post" && <ActionDialog key={dialog.note.id} note={dialog.note} action="post" onClose={() => setDialog(null)} />}
      {dialog?.kind === "reverse" && <ActionDialog key={dialog.note.id} note={dialog.note} action="reverse" onClose={() => setDialog(null)} />}
      {dialog?.kind === "apply" && <ApplyDialog key={dialog.note.id} note={dialog.note} onClose={() => setDialog(null)} />}
      {dialog?.kind === "unapply" && <UnapplyDialog key={dialog.applicationId} note={dialog.note} applicationId={dialog.applicationId} label={dialog.label} onClose={() => setDialog(null)} />}
    </section>
  );
}

/** Sau mỗi thao tác: sổ, hoá đơn, phiếu chi và khiếu nại đều có thể đổi — tải lại cả nhóm. */
function useRefreshLedgers() {
  const client = useQueryClient();
  return () => Promise.all(["gl-dn", "gl-dn-open", "gl-journals", "gl-ap", "gl-pay", "supplier-claims", "supplier-claim"].map((key) => client.invalidateQueries({ queryKey: [key] })));
}

function useSubmit(onDone: () => void) {
  const { t } = useTranslation();
  const refresh = useRefreshLedgers();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const run = async (fn: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      onDone();
    } catch (e) {
      setError(getLocalizedError(e, t, "gl.error"));
      await refresh();
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

function DialogFooter({ busy, disabled, label, onClose }: { busy: boolean; disabled: boolean; label: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 justify-end gap-2 border-t border-line p-4">
      <Button variant="secondary" disabled={busy} onClick={onClose}>
        {t("common.cancel")}
      </Button>
      <Button type="submit" disabled={busy || disabled}>
        {label}
      </Button>
    </div>
  );
}

function ActionDialog({ note, action, onClose }: { note: ApDebitNoteDto; action: "post" | "reverse"; onClose: () => void }) {
  const { t } = useTranslation();
  const [noteDate, setNoteDate] = useState(localToday);
  const [reason, setReason] = useState("");
  const { busy, error, run } = useSubmit(onClose);
  const post = action === "post";
  const invalid = reason.trim().length < 5 || (post && !/^\d{4}-\d{2}-\d{2}$/.test(noteDate));
  return (
    <Modal title={t(post ? "gl.dnPost" : "gl.dnReverse")} eyebrow={note.documentNo} closeLabel={t("common.close")} onClose={() => { if (!busy) onClose(); }} className="max-w-lg">
      <form
        className="flex min-h-0 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          void run(async () => {
            await apiFetch(`/finance/debit-notes/${note.id}/actions`, { method: "POST", body: { revision: note.revision, action, reason: reason.trim(), ...(post ? { noteDate } : {}) } });
          });
        }}
      >
        <div className="min-h-0 space-y-3 overflow-y-auto p-4 text-sm">
          <p>
            {note.claimNo} · {note.supplierName} · <span className="font-mono tabular">{money(note.amount, note.currency)}</span>
          </p>
          <p className="text-xs text-ink-soft">{t(post ? "gl.dnPostHint" : "gl.dnReverseHint")}</p>
          {post && (
            <div>
              <FieldLabel htmlFor="dn-date">{t("gl.dnDate")}</FieldLabel>
              <Input id="dn-date" type="date" className="max-w-48 font-mono" value={noteDate} disabled={busy} onChange={(e) => setNoteDate(e.target.value)} />
            </div>
          )}
          <div>
            <FieldLabel htmlFor="dn-reason">{t("gl.dnReason")}</FieldLabel>
            <Input id="dn-reason" value={reason} maxLength={1000} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </div>
          {error && <p role="alert" className="text-danger">{error}</p>}
        </div>
        <DialogFooter busy={busy} disabled={invalid} label={t(post ? "gl.post" : "gl.dnReverse")} onClose={onClose} />
      </form>
    </Modal>
  );
}

function UnapplyDialog({ note, applicationId, label, onClose }: { note: ApDebitNoteDto; applicationId: string; label: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const { busy, error, run } = useSubmit(onClose);
  const invalid = reason.trim().length < 5;
  return (
    <Modal title={t("gl.dnUnapply")} eyebrow={note.documentNo} closeLabel={t("common.close")} onClose={() => { if (!busy) onClose(); }} className="max-w-lg">
      <form
        className="flex min-h-0 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          void run(async () => {
            await apiFetch(`/finance/debit-notes/${note.id}/actions`, { method: "POST", body: { action: "unapply", applicationId, reason: reason.trim() } });
          });
        }}
      >
        <div className="min-h-0 space-y-3 overflow-y-auto p-4 text-sm">
          <p className="font-mono tabular">{label}</p>
          <div>
            <FieldLabel htmlFor="dn-unapply-reason">{t("gl.dnReason")}</FieldLabel>
            <Input id="dn-unapply-reason" value={reason} maxLength={1000} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </div>
          {error && <p role="alert" className="text-danger">{error}</p>}
        </div>
        <DialogFooter busy={busy} disabled={invalid} label={t("gl.dnUnapply")} onClose={onClose} />
      </form>
    </Modal>
  );
}

/**
 * Trừ phiếu vào hoá đơn: gợi ý cũ nhất trước (FIFO) tới hết phần chưa trừ; người dùng sửa được số.
 * Mỗi hoá đơn là một yêu cầu riêng với mã yêu cầu cố định, nên bấm lại sau lỗi mạng không trừ hai lần.
 */
function ApplyDialog({ note, onClose }: { note: ApDebitNoteDto; onClose: () => void }) {
  const { t } = useTranslation();
  const invoices = useQuery({ queryKey: ["gl-dn-open", note.id], queryFn: ({ signal }) => debitNoteOpenInvoices(note.id, signal) });
  const [amounts, setAmounts] = useState<Record<string, string> | null>(null);
  const [reason, setReason] = useState("");
  const requestIds = useRef(new Map<string, string>());
  const { busy, error, run, setError } = useSubmit(onClose);
  const rows = useMemo(() => invoices.data?.data ?? [], [invoices.data]);

  const suggested = useMemo(() => {
    let left = minor(note.unappliedAmount);
    const out: Record<string, string> = {};
    for (const inv of rows) {
      const take = Math.min(left, minor(inv.remaining));
      out[inv.id] = take > 0 ? fixed2(take) : "";
      left -= Math.max(0, take);
    }
    return out;
  }, [rows, note.unappliedAmount]);
  const values = amounts ?? suggested;

  const lineProblem = (inv: ApOpenInvoiceDto) => {
    const v = (values[inv.id] ?? "").trim();
    if (!v) return false;
    return !MONEY.test(v) || minor(v) > minor(inv.remaining);
  };
  const total = rows.reduce((sum, inv) => {
    const v = (values[inv.id] ?? "").trim();
    return sum + (v && MONEY.test(v) ? minor(v) : 0);
  }, 0);
  const invalid = rows.some(lineProblem) || total <= 0 || total > minor(note.unappliedAmount);

  const submit = () =>
    run(async () => {
      for (const inv of rows) {
        const v = (values[inv.id] ?? "").trim();
        if (!v || minor(v) === 0) continue;
        const requestId = requestIds.current.get(inv.id) ?? crypto.randomUUID();
        requestIds.current.set(inv.id, requestId);
        await apiFetch(`/finance/debit-notes/${note.id}/actions`, { method: "POST", body: { action: "apply", requestId, invoiceId: inv.id, amount: v, reason: reason.trim() } });
        // Dòng này đã xong: bỏ khỏi form để lần bấm lại (nếu dòng sau lỗi) không gửi lại nó.
        requestIds.current.delete(inv.id);
        setAmounts((prev) => ({ ...(prev ?? suggested), [inv.id]: "" }));
      }
    });

  return (
    <Modal title={t("gl.dnApply")} eyebrow={note.documentNo} closeLabel={t("common.close")} onClose={() => { if (!busy) onClose(); }} className="max-w-3xl">
      <form
        className="flex min-h-0 flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          if (invalid) return;
          setError(null);
          void submit();
        }}
      >
        <div className="min-h-0 space-y-3 overflow-y-auto p-4 text-sm">
          <p>
            {note.claimNo} · {note.supplierName} · {t("gl.dnUnapplied")}: <span className="font-mono tabular">{money(note.unappliedAmount, note.currency)}</span>
          </p>
          <p className="text-xs text-ink-soft">{t("gl.dnApplyHint")}</p>
          {invoices.isLoading ? (
            <LoadingState />
          ) : invoices.error ? (
            <ErrorState message={t("gl.error")} onRetry={() => void invoices.refetch()} />
          ) : rows.length === 0 ? (
            <p className="rounded-md bg-sunken p-3">{t("gl.dnNoOpen")}</p>
          ) : (
            <div className="overflow-x-auto rounded border border-line">
              <table className="w-full min-w-[560px]">
                <thead className="bg-sunken">
                  <tr>
                    <th className="p-2 text-left">{t("gl.dnInvoice")}</th>
                    <th className="p-2 text-left">{t("common.date")}</th>
                    <th className="p-2 text-right">{t("gl.dnRemaining")}</th>
                    <th className="p-2 text-right">{t("gl.dnApplyAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((inv) => (
                    <tr key={inv.id} className="border-t border-line">
                      <td className="p-2">
                        <span className="font-mono">{inv.documentNo}</span>
                        {inv.poNumber && <span className="block font-mono text-xs text-ink-soft">{inv.poNumber}</span>}
                      </td>
                      <td className="p-2 font-mono tabular">{inv.invoiceDate}</td>
                      <td className="p-2 text-right font-mono tabular">{money(inv.remaining, inv.currency)}</td>
                      <td className="p-2 text-right">
                        <Input
                          aria-label={`${t("gl.dnApplyAmount")} ${inv.documentNo}`}
                          className="ml-auto max-w-32 text-right font-mono tabular"
                          inputMode="decimal"
                          invalid={lineProblem(inv)}
                          value={values[inv.id] ?? ""}
                          disabled={busy}
                          onChange={(e) => setAmounts({ ...values, [inv.id]: e.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length > 0 && (
            <p className={total > minor(note.unappliedAmount) ? "text-danger" : ""}>
              {t("gl.dnApplyTotal")}: <span className="font-mono tabular">{formatMoney(total, note.currency)}</span>
            </p>
          )}
          <div>
            <FieldLabel htmlFor="dn-apply-reason">{t("gl.dnReason")}</FieldLabel>
            <Input id="dn-apply-reason" value={reason} maxLength={1000} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </div>
          {error && <p role="alert" className="text-danger">{error}</p>}
        </div>
        <DialogFooter busy={busy} disabled={invalid || rows.length === 0} label={t("gl.dnApply")} onClose={onClose} />
      </form>
    </Modal>
  );
}
